import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
} from '@mui/material';

import styles from './dynamic-params.module.scss';
import { ButtonCustom } from '../button-custom';
import { TextInputSmall } from '../text-input-small';

interface DynamicParam {
  id: string;
  fieldName: string;
  fieldValue: string[];
}

interface DynamicParamsProps {
  value: DynamicParam[];
  onChange: (params: DynamicParam[]) => void;
  disabled?: boolean;
  error?: boolean;
}

export const DynamicParams: React.FC<DynamicParamsProps> = ({
  value = [],
  onChange,
  disabled = false,
  error = false,
}) => {
  const [params, setParams] = useState<DynamicParam[]>(value);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; paramId: string | null }>({
    open: false,
    paramId: null,
  });
  const [newValueInputs, setNewValueInputs] = useState<{ [paramId: string]: string }>({});

  // Синхронизируем локальное состояние с пропсом
  useEffect(() => {
    setParams(value);
  }, [value]);

  const generateId = useCallback(() => {
    return `param_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addParam = useCallback(() => {
    const newParam: DynamicParam = {
      id: generateId(),
      fieldName: '',
      fieldValue: [],
    };
    const updatedParams = [...params, newParam];
    setParams(updatedParams);
    onChange(updatedParams);
  }, [params, onChange, generateId]);

  const updateParamName = useCallback(
    (id: string, fieldName: string) => {
      const updatedParams = params.map((param) =>
        param.id === id ? { ...param, fieldName } : param,
      );
      setParams(updatedParams);
      onChange(updatedParams);
    },
    [params, onChange],
  );

  const addValueToParam = useCallback(
    (paramId: string) => {
      const newValue = newValueInputs[paramId]?.trim();
      if (!newValue) return;

      const updatedParams = params.map((param) =>
        param.id === paramId ? { ...param, fieldValue: [...param.fieldValue, newValue] } : param,
      );
      setParams(updatedParams);
      onChange(updatedParams);
      setNewValueInputs((prev) => ({ ...prev, [paramId]: '' }));
    },
    [params, onChange, newValueInputs],
  );

  const removeValueFromParam = useCallback(
    (paramId: string, valueIndex: number) => {
      const updatedParams = params.map((param) =>
        param.id === paramId
          ? { ...param, fieldValue: param.fieldValue.filter((_, index) => index !== valueIndex) }
          : param,
      );
      setParams(updatedParams);
      onChange(updatedParams);
    },
    [params, onChange],
  );

  const updateNewValueInput = useCallback((paramId: string, value: string) => {
    setNewValueInputs((prev) => ({ ...prev, [paramId]: value }));
  }, []);

  const confirmDelete = useCallback((id: string) => {
    setDeleteConfirm({ open: true, paramId: id });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteConfirm.paramId) {
      const updatedParams = params.filter((param) => param.id !== deleteConfirm.paramId);
      setParams(updatedParams);
      onChange(updatedParams);
    }
    setDeleteConfirm({ open: false, paramId: null });
  }, [params, onChange, deleteConfirm.paramId]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirm({ open: false, paramId: null });
  }, []);

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Typography variant='body1' className={styles.title}>
          Параметры транспортного средства
        </Typography>
        <Button
          variant='outlined'
          size='small'
          onClick={addParam}
          disabled={disabled}
          className={styles.addButton}
        >
          + Добавить параметр
        </Button>
      </Box>

      {params.length === 0 ? (
        <Box className={styles.emptyState}>
          <Typography variant='body2' color='textSecondary'>
            Параметры не добавлены. Нажмите "Добавить параметр" для создания нового поля.
          </Typography>
        </Box>
      ) : (
        <Box className={styles.paramsList}>
          {params.map((param) => (
            <Box key={param.id} className={styles.paramRow}>
              <Box className={styles.paramContainer}>
                <Box className={styles.paramFields}>
                  <TextInputSmall
                    value={param.fieldName}
                    onChange={(e) => updateParamName(param.id, e.target.value)}
                    placeholder='Название параметра'
                    disabled={disabled}
                    error={error && !param.fieldName.trim()}
                    className={styles.paramNameField}
                  />
                  <Button
                    onClick={() => confirmDelete(param.id)}
                    disabled={disabled}
                    size='small'
                    variant='outlined'
                    color='error'
                    className={styles.deleteButton}
                  >
                    ✕
                  </Button>
                </Box>

                {/* Отображение существующих значений */}
                {param.fieldValue.length > 0 && (
                  <Box className={styles.valuesContainer}>
                    {param.fieldValue.map((value, index) => (
                      <Chip
                        key={index}
                        label={value}
                        onDelete={
                          disabled ? undefined : () => removeValueFromParam(param.id, index)
                        }
                        size='small'
                        className={styles.valueChip}
                      />
                    ))}
                  </Box>
                )}

                {/* Поле для добавления нового значения */}
                {!disabled && (
                  <Box className={styles.paramFields}>
                    <TextInputSmall
                      value={newValueInputs[param.id] || ''}
                      onChange={(e) => updateNewValueInput(param.id, e.target.value)}
                      placeholder='Добавить значение'
                      className={styles.addValueField}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addValueToParam(param.id);
                        }
                      }}
                    />
                    <Button
                      onClick={() => addValueToParam(param.id)}
                      size='small'
                      variant='outlined'
                      disabled={!newValueInputs[param.id]?.trim()}
                      className={styles.addValueButton}
                    >
                      +
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Диалог подтверждения удаления */}
      <Dialog
        open={deleteConfirm.open}
        onClose={handleDeleteCancel}
        aria-labelledby='delete-confirm-title'
        aria-describedby='delete-confirm-description'
        sx={{
          '.MuiDialog-paper': {
            padding: '20px',
            borderRadius: '10px',
            maxWidth: '400px',
          },
        }}
      >
        <DialogTitle id='delete-confirm-title'>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <DialogContentText id='delete-confirm-description'>
            Вы уверены, что хотите удалить этот параметр? Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <ButtonCustom onClick={handleDeleteCancel} variant='outlined'>
            Отмена
          </ButtonCustom>
          <ButtonCustom onClick={handleDeleteConfirm} color='error'>
            Удалить
          </ButtonCustom>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
